import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset — please sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-6 lg:px-8 py-20" data-testid="reset-password-page">
      <Seo title="Reset password — BizzAtlas" description="Set a new password for your BizzAtlas account." />
      <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Password</div>
      <h1 className="font-display text-4xl tracking-tight">Reset your password.</h1>
      {!token && (
        <div className="mt-6 text-sm text-red-600">Missing token — open the link from the email.</div>
      )}
      {done ? (
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-800" data-testid="reset-done">
          <CheckCircle2 className="h-6 w-6 mb-2" />
          <div className="font-display text-xl">Password updated.</div>
          <p className="text-sm mt-1">Redirecting to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-2"><Label htmlFor="rp-new">New password</Label><Input id="rp-new" type="password" minLength={6} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new" /></div>
          <div className="space-y-2"><Label htmlFor="rp-new2">Confirm new password</Label><Input id="rp-new2" type="password" minLength={6} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-new2" /></div>
          {error && <div className="text-sm text-red-600" data-testid="reset-error">{error}</div>}
          <Button type="submit" disabled={busy || !token} className="w-full btn-brand" data-testid="reset-submit">{busy ? "Updating…" : "Set new password"}</Button>
        </form>
      )}
      <div className="mt-6 text-sm text-zinc-500">
        <Link to="/login" className="underline text-zinc-900">Back to sign in</Link>
      </div>
    </div>
  );
}
